import React, { useEffect, useState } from "react";
import { Drawer } from "@mantine/core";
import moment from "moment";
import Icon from "./icons";
import { updateAttendenceStaff } from "../../../services/staff";
import { decrypt } from "../../../utils";
import "./SchedulerCreate.css";

// Pull the client's service list (servicenumber + servicetitle) off the entry's
// populated client.form1 (encrypted JSON), for the Service selector.
const servicesOf = (record) => {
  try {
    const f1 = typeof record?.client?.form1 === "string" ? decrypt(record.client.form1) : record?.client?.form1;
    return Array.isArray(f1?.services) ? f1.services : [];
  } catch {
    return [];
  }
};

const clientNameOf = (record) => {
  const c = record?.client;
  if (!c) return "";
  try {
    const f1 = typeof c.form1 === "string" ? decrypt(c.form1) : c.form1;
    if (f1?.firstName || f1?.lastName) return `${f1.firstName || ""} ${f1.lastName || ""}`.trim();
  } catch { /* ignore */ }
  if (record?.clientName) return record.clientName;
  if (typeof c.name === "string") { try { return decrypt(c.name) || c.name; } catch { return c.name; } }
  return c.name || "";
};

const currentServices = (record) =>
  Array.isArray(record?.services) && record.services.length ? record.services : record?.service ? [record.service] : [];

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);

// Edit the clock in/out times of a single time-card entry. Times are read and
// written as wall-clock times in the staff's local zone — the same convention
// the Time Card / daily-entries tables use (plain `moment`). The backend's
// `update-attendance` time-edit branch re-parses the "hh:mm:ss A" string in the
// facility timezone, which matches the staff's zone in normal use, so edits
// round-trip cleanly. Optionally resubmits on save.
export default function TimeEntryEditDrawer({ opened, record, onClose, onSaved, onBack }) {
  const [clockIn, setClockIn] = useState("09:00");
  const [clockOut, setClockOut] = useState("17:00");
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!opened || !record) return;
    setClockIn(record.startTime ? moment(record.startTime).format("HH:mm") : "09:00");
    setClockOut(record.endTime ? moment(record.endTime).format("HH:mm") : "17:00");
    setServices(currentServices(record));
    setErr("");
  }, [opened, record]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!record) return null;
  const dateStr = record.date?.date
    ? moment(record.date.date).format("dddd, MMM D, YYYY")
    : record.startTime ? moment(record.startTime).format("dddd, MMM D, YYYY") : "";
  const serviceOptions = servicesOf(record);
  const isClient = record.type !== "facility";
  const cName = isClient ? clientNameOf(record) : "";
  const toggleService = (code) =>
    setServices((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const save = async () => {
    if (!clockIn || !clockOut) { setErr("Both clock in and clock out are required."); return; }
    // Anchor on the entry's existing calendar day (local wall-clock, matching
    // the table display) and apply the new clock times. Send absolute instants
    // so the backend stores them verbatim instead of re-parsing a time-of-day
    // string in the facility timezone.
    const baseDay = record.date?.date || record.startTime;
    const [ih, im] = clockIn.split(":").map(Number);
    const [oh, om] = clockOut.split(":").map(Number);
    const start = moment(baseDay).hour(ih).minute(im).second(0).millisecond(0);
    const end = moment(baseDay).hour(oh).minute(om).second(0).millisecond(0);
    // Overnight: clock-out at/before clock-in rolls to the next day.
    if (!end.isAfter(start)) end.add(1, "day");
    setBusy(true);
    const payload = { id: idOf(record), startTimeFull: start.toISOString(), endTimeFull: end.toISOString() };
    // Only send services when they changed, so unrelated edits don't touch them.
    const orig = currentServices(record);
    if (JSON.stringify([...services].sort()) !== JSON.stringify([...orig].sort())) payload.services = services;
    await updateAttendenceStaff([payload]);
    setBusy(false);
    onSaved?.();
    onClose?.();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={460}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      zIndex={520}
    >
      <div className="sd-root">
        <div className="sd-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {onBack ? (
              <button type="button" className="sd-close" onClick={onBack} aria-label="Back to daily entries"><Icon name="arrowLeft" size={16} /></button>
            ) : null}
            <div>
              <h3>Edit Time Entry</h3>
              <div className="dh-sub">{dateStr}</div>
            </div>
          </div>
          <button type="button" className="sd-close" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className="sd-body sd-create" style={{ display: "flex", flexDirection: "column" }}>
          {isClient && cName ? (
            <div className="field">
              <label>Client</label>
              <div className="ro-field">{cName}</div>
            </div>
          ) : null}

          {serviceOptions.length > 0 ? (
            <div className="field">
              <label>Service{serviceOptions.length > 1 ? "s" : ""} <span style={{ color: "var(--app-text-muted)", fontWeight: 500 }}>· select all that apply</span></label>
              <div className="svc-pills">
                {serviceOptions.map((s) => {
                  const on = services.includes(s.servicenumber);
                  return (
                    <button type="button" key={s.servicenumber} className={`svc-pill ${on ? "on" : ""}`} onClick={() => toggleService(s.servicenumber)}>
                      {s.servicetitle ? `${s.servicetitle} (${s.servicenumber})` : s.servicenumber}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="frow">
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Clock in <span className="req">*</span></label>
              <input className="inp" type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Clock out <span className="req">*</span></label>
              <input className="inp" type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)} />
            </div>
          </div>

          {err ? <div style={{ color: "#dc2626", fontSize: 12.5, marginTop: 12 }}>{err}</div> : null}

          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--app-text-muted)", marginTop: "auto", paddingTop: 18 }}>
            <Icon name="pin" size={15} /> Edited entries are flagged for admin review.
          </div>
        </div>

        <div className="sd-foot">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <span className="spacer" />
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy || !clockIn || !clockOut}>
            <Icon name="check" size={16} /> Save changes
          </button>
        </div>
      </div>
    </Drawer>
  );
}
